<script setup>
import { ref, computed, watchEffect, onMounted, watch, onBeforeUnmount } from 'vue'

import CheckboxInput from '@/components/input/checkboxInput.vue'
import CheckIconInput from '@/components/input/checkIconInput.vue'

import { useTableCsv } from './useTableCsv.js'

const emit = defineEmits(['update:selectedItems', 'rowClick'])

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
  tableTitle: {
    type: String,
    default: '',
  },
  tableDescription: {
    type: String,
    default: '',
  },
  tableHeaderEnabled: {
    type: Boolean,
    default: false,
  },
  fields: {
    type: Array,
    required: true,
  },
  csvEnabled: {
    type: Boolean,
    default: false,
  },
  emptyMessage: {
    type: String,
    default: 'Nenhum dado encontrado!',
  },
  selectableEnabled: {
    type: Boolean,
    default: false,
  },
  clickableRow: {
    type: Boolean,
    default: false,
  },
  csvFileName: {
    type: String,
    default: '',
  },
  rowErrors: {
    type: Object,
    required: false,
  },
  rowClass: {
    type: [Function, String],
    default: '',
  },
})

const resolveRowClass = item =>
  typeof props.rowClass === 'function' ? props.rowClass(item) : props.rowClass

// Models
const selectedItems = defineModel({ default: {}, deep: true, type: Object })

// Refs
const tableRef = ref(null)
const lastColWidth = ref(0)
const firstColWidth = ref(0)
const tableWrapperRef = ref(null)
const hasHorizontalScroll = ref(false)
const isScrolledFromLeft = ref(false)
const isScrolledToRight = ref(false)
const currentHeaderCheckStatus = ref('empty')
const showAll = ref(true)

let resizeObserver = null

// Computeds
const selectedLength = computed(() => Object.keys(selectedItems.value).length)
const allIdsForCurrentPage = computed(() => props.data.map(item => item.id))
const allSelectedIds = computed(() => Object.keys(selectedItems.value))
const hasAnySelectedInCurrentPage = computed(() =>
  allIdsForCurrentPage.value.some(id => id in selectedItems.value),
)

const isCurrentPageAllSelected = computed(() => {
  if (selectedLength.value === 0) return false
  return allIdsForCurrentPage.value.every(id => id in selectedItems.value)
})

const hasSelectedInOtherPage = computed(() => {
  const allIdsForCurrentPageSet = new Set(allIdsForCurrentPage.value)
  const allSelectedIdsSet = new Set(allSelectedIds.value)
  const selectedIdsFromOtherPages = allSelectedIdsSet.difference(allIdsForCurrentPageSet)
  return Boolean(selectedIdsFromOtherPages.size)
})

const tableData = computed(() => (showAll.value ? props.data : Object.values(selectedItems.value)))

const stickyField = computed(() => {
  const last = props.fields?.[props.fields.length - 1]
  return last?.sticky === true ? last : null
})

watch(
  selectedItems,
  val => {
    emit('update:selectedItems', val)
  },
  { deep: true },
)

// Watchers
watchEffect(() => {
  currentHeaderCheckStatus.value = isCurrentPageAllSelected.value
    ? 'full'
    : hasAnySelectedInCurrentPage.value
      ? 'intermediary'
      : 'empty'
})

// Methods

const { createCsv } = useTableCsv(tableRef, () => props.csvFileName)

// Selection helpers
const cleanSelectedItems = () => {
  selectedItems.value = {}
  showAll.value = true
}

const handleHeaderCheckAction = (oldStatus, newStatus) => {
  if (
    newStatus === 'intermediary' ||
    oldStatus === newStatus ||
    (oldStatus === 'intermediary' && newStatus === 'empty')
  ) {
    return
  }
  const isInsert = newStatus === 'full'
  props.data.forEach(item => {
    handleRowCheckAction(isInsert, item)
  })
}

const handleRowCheckAction = (insert, item) => {
  if (!item?.id) return
  if (insert) selectedItems.value[item.id] = item
  else Reflect.deleteProperty(selectedItems.value, item.id)
}

const showAllItems = () => (showAll.value = true)
const showSelectedItems = () => (showAll.value = false)

const handleRowClick = item => {
  if (props.clickableRow) emit('rowClick', item)
}

// Measuring sticky columns
const measureLastCol = () => {
  if (!tableRef.value) return

  // Medir última coluna se sticky
  if (stickyField.value) {
    const lastTh = tableRef.value.querySelector('thead tr th:last-child')
    if (lastTh) lastColWidth.value = lastTh.offsetWidth
  }

  // Medir primeira coluna se selectableEnabled (sticky left)
  if (props.selectableEnabled) {
    const firstTh = tableRef.value.querySelector('thead tr th:first-child')
    if (firstTh) firstColWidth.value = firstTh.offsetWidth
  }
}

// Scroll detection & shadows
const checkScroll = () => {
  const el = tableWrapperRef.value
  if (!el) return

  const hasScroll = el.scrollWidth > el.clientWidth + 1
  hasHorizontalScroll.value = hasScroll

  isScrolledFromLeft.value = hasScroll && el.scrollLeft > 1
  isScrolledToRight.value = hasScroll && el.scrollWidth - el.clientWidth - el.scrollLeft > 1
}

// Update all measurements (sticky column width + scroll state)
const updateMeasurements = () => {
  measureLastCol()
  checkScroll()
}

// Lifecycle
onMounted(() => {
  const table = tableRef.value
  const wrapper = tableWrapperRef.value

  // Medições iniciais
  updateMeasurements()

  // Setup ResizeObserver
  if (table || wrapper) {
    resizeObserver = new ResizeObserver(updateMeasurements)

    if ((stickyField.value || props.selectableEnabled) && table) resizeObserver.observe(table)
    if (wrapper) resizeObserver.observe(wrapper)
  }

  // Scroll listener
  wrapper?.addEventListener('scroll', checkScroll, { passive: true })
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  tableWrapperRef.value?.removeEventListener('scroll', checkScroll)
})

defineExpose({ createCsv })
</script>

<template>
  <div class="border-neutral-30 flex w-full flex-col rounded-2xl border">
    <!-- Header -->
    <div
      v-if="tableHeaderEnabled"
      class="flex w-full flex-col gap-3 border-b border-[#EAECF0] px-6 py-4 lg:flex-row lg:items-center lg:justify-between lg:gap-4"
    >
      <div class="flex min-w-0 flex-col gap-1">
        <h2 v-if="tableTitle" class="text-xl font-semibold text-neutral-100">
          {{ tableTitle }}
        </h2>
        <p v-if="tableDescription" class="text-neutral-60 text-sm">
          {{ tableDescription }}
        </p>
      </div>
      <div class="flex w-full lg:w-auto">
        <slot name="tableActions" />
      </div>
    </div>

    <!-- Filtros e busca -->
    <div
      v-if="selectableEnabled"
      class="flex flex-wrap items-center justify-between gap-2 border-b border-[#EAECF0] px-6 py-3"
    >
      <div class="inline-flex rounded-xl shadow-xs" role="group">
        <ImoButton
          text="Tudo"
          variant="tertiary"
          size="sm"
          class="rounded-r-none! border-r-0!"
          :class="{ 'bg-neutral-20!': showAll }"
          @click="showAllItems"
        />
        <ImoButton
          :text="`Selecionados (${selectedLength})`"
          variant="tertiary"
          size="sm"
          class="rounded-l-none! border-l-0!"
          :class="{ 'bg-neutral-20!': !showAll }"
          @click="showSelectedItems"
        />
      </div>
      <div class="mr-auto flex gap-2">
        <ImoButton
          text="Selecionar todos da página"
          variant="tertiary"
          size="sm"
          @click="() => handleHeaderCheckAction('empty', 'full')"
          :disabled="!showAll || isCurrentPageAllSelected"
        />
        <ImoButton
          text="Limpar selecionados da página"
          variant="tertiary"
          size="sm"
          :disabled="!showAll || !hasAnySelectedInCurrentPage"
          @click="() => handleHeaderCheckAction('full', 'empty')"
        />
        <ImoButton
          text="Limpar todos os itens selecionados"
          variant="tertiary"
          size="sm"
          @click="cleanSelectedItems"
          :disabled="!hasSelectedInOtherPage"
        />
      </div>
    </div>

    <div
      class="border-neutral-30 border-t-0"
      :class="{
        'overflow-hidden': tableData.length > 0,
      }"
    >
      <div class="relative">
        <div
          v-if="tableData.length > 0"
          v-show="isScrolledFromLeft"
          class="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 transition-opacity duration-100"
          :class="{
            'bottom-4': hasHorizontalScroll,
            'rounded-tl-2xl': !selectableEnabled && !tableHeaderEnabled,
          }"
          :style="{
            left: selectableEnabled ? `${firstColWidth}px` : '0px',
            background: 'linear-gradient(to right, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))',
          }"
        />

        <div
          v-if="tableData.length > 0"
          v-show="isScrolledToRight"
          class="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 transition-opacity duration-100"
          :class="{
            'bottom-4': hasHorizontalScroll,
            'rounded-tr-2xl': !stickyField && !tableHeaderEnabled,
          }"
          :style="{
            right: stickyField ? `${lastColWidth}px` : '0px',
            background: 'linear-gradient(to left, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0))',
          }"
        />

        <div
          ref="tableWrapperRef"
          class="w-full overflow-x-auto"
          :class="{ 'rounded-2xl rounded-b-none': !tableHeaderEnabled }"
        >
          <table v-if="tableData.length > 0" ref="tableRef" class="w-full items-center">
            <thead
              class="text-neutral-60 bg-neutral-0 hidden rounded-2xl border-[#EAECF0] md:table-header-group"
            >
              <tr class="">
                <th
                  v-for="(field, i) in fields"
                  :key="`head-${i}`"
                  scope="col"
                  class="h-11 border-b border-[#EAECF0] px-6 py-3 text-left text-sm font-semibold whitespace-nowrap"
                  :class="{
                    'bg-neutral-0 sticky top-0 left-0':
                      i === 0 && selectableEnabled && tableData.length > 0,
                    'rounded-none': tableHeaderEnabled,
                    'bg-neutral-0 sticky top-0 right-0': field.sticky && tableData.length > 0,
                    'bg-neutral-0 static top-0 right-0': tableData.length == 0,
                  }"
                >
                  <span class="flex flex-row gap-3">
                    <CheckIconInput
                      v-if="selectableEnabled && i === 0 && showAll"
                      v-model="currentHeaderCheckStatus"
                      @statusChange="handleHeaderCheckAction"
                    />
                    <slot :name="`head(${field.key})`" :item="field">
                      {{ field.label }}
                    </slot>
                  </span>
                </th>
              </tr>
            </thead>

            <tbody v-if="tableData.length">
              <tr
                v-for="(item, i) in tableData"
                :key="`r-${i}`"
                class="my-2 md:border md:border-x-0 md:border-t-0 md:border-[#EAECF0]"
                :class="[
                  {
                    'even:contrast-110': item.id in selectedItems,
                    error:
                      rowErrors &&
                      rowErrors.errorsIndex.includes(
                        `${(rowErrors.currentPage - 1) * Number(rowErrors.perPage) + i}`,
                      ),
                    'hover:bg-neutral-0 cursor-pointer': clickableRow,
                  },
                  resolveRowClass(item),
                ]"
                @click="handleRowClick(item)"
              >
                <td
                  v-for="(field, j) in fields"
                  :data-label="field.label"
                  :key="`j-${j}`"
                  class="flex flex-col items-start justify-between gap-3 p-3 px-6 leading-4 md:table-cell md:flex-row md:items-end md:truncate md:border-none md:whitespace-nowrap md:before:hidden last:md:bg-white"
                  :class="{
                    'cursor-pointer': selectableEnabled,
                    'sticky top-0 left-0': j === 0 && selectableEnabled,
                    'sticky top-0 right-0': field.sticky,
                    'last:bg-neutral-10 bg-white': !(item.id in selectedItems),
                    'bg-neutral-20!': item.id in selectedItems,
                  }"
                >
                  <div class="text-neutral-60 w-full text-xs font-bold md:text-sm md:font-normal">
                    <div class="flex w-full flex-row gap-3" :class="`${field.align}`">
                      <CheckboxInput
                        v-if="selectableEnabled && j === 0"
                        :name="`row-${item.id}`"
                        :key="`row-${item.id}`"
                        :model-value="item.id in selectedItems"
                        @update:model-value="checked => handleRowCheckAction(checked, item)"
                        :aria-label="`Selecionar linha ${i + 1}`"
                        size="size-[16px]"
                      />

                      <slot :name="`cell(${field.key})`" :item="item" :row="i" :column="j">
                        {{
                          field.formater && item[field.key] != null
                            ? field.formater(item[field.key])
                            : item[field.key]
                        }}
                      </slot>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>

            <!--
            <tbody v-else class="bg-white">
              <tr>
                <td colspan="100%" class="py-16 text-center">
                  <div class="flex flex-col items-center justify-center gap-2">
                    <div class="text-4xl text-orange-600">⌖</div>
                    <h3 class="text-lg font-semibold text-neutral-100">
                      {{ showAll ? emptyMessage : 'Nenhum item selecionado!' }}
                    </h3>
                  </div>
                </td>
              </tr>
            </tbody>
            -->
          </table>

          <template v-if="tableData.length === 0">
            <div class="flex flex-col items-center justify-center gap-2 py-12">
              <div class="text-4xl text-orange-600">⌖</div>
              <h3 class="text-lg font-semibold text-neutral-100">
                {{ showAll ? emptyMessage : 'Nenhum item selecionado!' }}
              </h3>
            </div>
          </template>

          <!-- <template v-if="tableData.length > 0">
            <div class="border-fixes-left"></div>
            <div class="border-fixes-right"></div>
          </template> -->
        </div>
      </div>

      <div v-if="data.length && showAll" class="flex flex-row justify-between px-6 py-3">
        <div class="flex items-center">
          <ImoButton
            v-if="csvEnabled && data.length"
            @click="createCsv"
            icon="document-download"
            variant="tertiary"
            size="sm"
            text="Baixar CSV"
          />
        </div>
        <slot name="pagination" />
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

table {
  td {
    &:before {
      content: attr(data-label);
      text-align: left;
      color: #475569;
      font-weight: 400;
      font-size: 12px;
    }
  }

  thead {
    tr {
      th {
        &:last-child {
          padding-left: 12px;
          padding-right: 12px;
        }
      }
    }
  }
  tbody {
    tr {
      td {
        &:last-child {
          padding-left: 12px;
          padding-right: 12px;

          & > div {
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;

            & > div {
              padding: 8px;
            }
          }
        }
      }
    }
  }
}

.border-fixes-left,
.border-fixes-right {
  top: -6px;
  position: absolute;

  &:before {
    display: block;
    content: '';
    padding: 5px;
    border: solid 1px #cbd5e1;
    border-bottom: none;
    border-radius: 16px;

    @media (width >= 48rem) {
      background: #f8fafc;
    }
  }
}

.border-fixes-left {
  padding: 5px 0px 0px 5px;
  left: -6px;

  &:before {
    border-right: none;
  }
}

.border-fixes-right {
  padding: 5px 5px 0px 0px;
  right: -6px;

  &:before {
    border-left: none;
  }
}

.error {
  @apply bg-error-10 border-error-60 text-error-60 border-none;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
